import React, {useRef, useState} from "react";
import styled from "styled-components";
import {range} from "../Utility/utilities";
import {InventoryQuantity, useInventory} from "../State/InventoryContext";
import {CheatPlacementOptions, EmptyMinerPopup, EmptySlot} from "../SharedComponents/EmptySlot";
import {InventoryPropertiesEnum, ItemQuantity} from "../State/Inventory";
import {BsFuelPump} from "react-icons/bs";
import {useOutsideAlerter} from "../Utility/useOutsideAlerter";
import {MachineCard} from "../SharedComponents/MachineCard";
import {SMELTER_RECIPES, ENERGY_COSTS} from "../State/GameData";
import {FieldHeader} from "../SharedComponents/FieldHeader";
import {useNavigate} from "react-router-dom";
import {SmeltingUnlockModal} from "./SmeltingUnlockModal";
import {useCheatKey} from "../Utility/useCheatKey";

const F = {
    FuelControlContainer: styled.div`
      width: 540px;
      margin-bottom: 16px;
      padding: 12px;
      background: #f5f5f5;
      border-radius: 4px;
      border: 2px solid #ddd;
      display: flex;
      align-items: center;
      justify-content: space-between;
    `,
    FuelControlLabel: styled.div`
      font-size: 14px;
      font-weight: bold;
      color: #333;
    `,
    FuelControlButtons: styled.div`
      display: flex;
      align-items: center;
      gap: 8px;
    `,
    FuelControlButton: styled.button`
      width: 32px;
      height: 32px;
      border-radius: 4px;
      border: 2px solid #666;
      background: #fff;
      color: #333;
      font-size: 18px;
      font-weight: bold;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      &:hover {
        background: #e0e0e0;
      }
      &:active {
        transform: scale(0.95);
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `,
    FuelControlValue: styled.div`
      font-size: 16px;
      font-weight: bold;
      color: #ff9800;
      min-width: 40px;
      text-align: center;
    `,
    FieldContainer: styled.div`
      width: 651px;
      margin-bottom: 32px;
    `,
    Smelter: styled.div<{ $type: string }>`
      border-radius: 4px;
      width: 651px;
      height: 253px;
      padding-top: 16px;
      padding-bottom: 16px;
      padding-left: 16px;
      display: flex;
      justify-content: space-between;
      ${(props) => ((props.$type === "copper") &&
                      `background-color: #E0791E;
            border: 2px solid black;`)
              || ((props.$type === "iron") && `
            background-color: #8D918D;
            border: 2px solid black;
            `)
              || ((props.$type === "stone") && `
            background-color: #C0B8A8;
            border: 2px solid black;
            `)
              || ((props.$type === "steel") && `
            background-color: #6B7A8F;
            border: 2px solid black;
            `)
      }
    `,
    SmelterInner: styled.div`
      width: 530px;
      display: flex;
      flex-wrap: wrap;
    `,

    FuelSelect: styled.div`
      width: 80px;
      display: flex;
      flex-direction: column;
      padding-bottom: 16px;
      align-items: center;
    `,
    ManualButton: styled.div`
      background-color: lightgrey;
      border-radius: 8px;
      border: 4px solid white;
      width: 70px;
      height: 70px;
      cursor: pointer;
      box-shadow: 2px 2px;
    `,
    LockedSlot: styled.div`
      background-color: #d9d9d9;
      border-radius: 8px;
      border: 4px dashed #b0b0b0;
      color: #6c757d;
      width: 90px;
      height: 100px;
      margin-right: 16px;
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      text-align: center;
      cursor: pointer;
      transition: background-color 0.2s ease, color 0.2s ease;
      &:hover {
        background-color: #c4c4c4;
        color: #464646;
      }
    `,
    PatchStatus: styled.small`
      display: block;
      margin-left: 8px;
      margin-bottom: 8px;
    `,
}

const SMELTER_TITLES: Record<string, string> = {
    stone: "Baking Stone to Bricks",
}


function FuelSelection(props: { type: string, options: InventoryQuantity[], selectedFuel: string | null }) {
    const {addFuelToSmelter} = useInventory()
    const [popupIsOpen, setPopupIsOpen] = useState(false);
    const wrapperRef = useRef(null)
    const closePopup = () => {
        setPopupIsOpen(false);
    }
    useOutsideAlerter(wrapperRef, closePopup)

    const pickedFuel = (itemId: string) => {
        addFuelToSmelter(props.type, itemId)
    }

    return <F.FuelSelect>
        <F.ManualButton ref={wrapperRef}>
            <EmptyMinerPopup isOpen={popupIsOpen} callback={pickedFuel} options={props.options}/>
            <BsFuelPump size={62}
                        onClick={() => setPopupIsOpen(true)}
            />
            {props.selectedFuel ? props.selectedFuel : "None"}
        </F.ManualButton>
    </F.FuelSelect>
}

function FuelControl() {
    const {inventory, setFuelAmountPerClick} = useInventory();
    const currentAmount = inventory.fuelAmountPerClick ?? 1;

    const decrease = () => {
        if (currentAmount > 1) {
            setFuelAmountPerClick(currentAmount - 1);
        }
    };

    const increase = () => {
        if (currentAmount < 10) {
            setFuelAmountPerClick(currentAmount + 1);
        }
    };

    return (
        <F.FuelControlContainer>
            <F.FuelControlLabel>Fuel per Click:</F.FuelControlLabel>
            <F.FuelControlButtons>
                <F.FuelControlButton onClick={decrease} disabled={currentAmount <= 1}>
                    −
                </F.FuelControlButton>
                <F.FuelControlValue>{currentAmount}</F.FuelControlValue>
                <F.FuelControlButton onClick={increase} disabled={currentAmount >= 10}>
                    +
                </F.FuelControlButton>
            </F.FuelControlButtons>
        </F.FuelControlContainer>
    );
}

function Smelter(props: { type: string }) {
    const {inventory, toggleSmelter, getItemsByType, addSmelter, updateInventoryItem, getItemQuantity, hasTechnology, addFuelToSmelterBuffer, removeSmelter, completeSmelterUnlock} = useInventory()
    const fieldState = inventory.smelterState.find(it => it.resourceType === props.type)
    if (!fieldState) throw new Error("invalid Field!")
    const nrOfCurrentSmelters = inventory.smelterState.find((it) => it.resourceType === props.type)!.smelters.length
    const navigate = useNavigate();
    const [showUnlockModal, setShowUnlockModal] = useState(false);
    const cheatActive = useCheatKey("c");

    const hasAutoFueling = hasTechnology("tech-auto-smelter-fueling");
    const fuelOptions = getItemsByType(InventoryPropertiesEnum.fuel)
    const recipe = SMELTER_RECIPES[props.type]
    const fuelCostPerCycle = recipe?.fuelCostPerCycle

    const smelters = inventory.inventory
        .filter(invQ => invQ?.item.properties.includes(InventoryPropertiesEnum.smelter))

    const clearedSpots = fieldState.clearedSpots ?? 0;
    const maxSlots = Math.min(10, clearedSpots);
    const lockedSlots = Math.max(0, 10 - clearedSpots);
    const canAddMoreSmelters = nrOfCurrentSmelters < maxSlots;
    const fillerSlots = Math.max(maxSlots - nrOfCurrentSmelters - (canAddMoreSmelters ? 1 : 0), 0);
    const foundationUnlockingEnabled = hasTechnology("tech-furnace-foundations");

    const handleUnlockComplete = (rewards: ItemQuantity[]) => {
        completeSmelterUnlock(props.type, rewards);
        setShowUnlockModal(false);
    };

    const handleUnlockClick = () => {
        if (!foundationUnlockingEnabled) {
            return;
        }
        if (cheatActive) {
            completeSmelterUnlock(props.type, []);
            setShowUnlockModal(false);
            return;
        }
        setShowUnlockModal(true);
    };

    const addedSmelter = (itemId: string, options?: CheatPlacementOptions) => {
        addSmelter(props.type, itemId)
        if (!options?.isCheat) {
            updateInventoryItem(itemId, -1);
        }
    }

    const smeltersRunning = fieldState.smelters.filter(s => s.running).length
    const productionPerSecond = recipe ? fieldState.smelters.reduce((sum, smelter) => {
        if (!smelter.running) {
            return sum
        }
        return sum + (recipe.outputQuantity / recipe.cycleTimeMs) * 1000
    }, 0) : 0
    const coalPerSecond = recipe && fuelCostPerCycle ? fieldState.smelters.reduce((sum, smelter) => {
        if (!smelter.running) {
            return sum
        }
        return sum + (fuelCostPerCycle.quantity / recipe.cycleTimeMs) * 1000
    }, 0) : 0
    const requestCraft = (itemId: string) => {
        navigate("/create", {state: {targetItemId: itemId}});
    }

    return <F.FieldContainer>
        <FieldHeader
            title={SMELTER_TITLES[props.type] ?? `${props.type.charAt(0).toUpperCase() + props.type.slice(1)} Smelting`}
            running={smeltersRunning}
            total={10}
            productionPerSecond={productionPerSecond}
            fuelPerSecond={coalPerSecond}
        />
        <F.PatchStatus>{clearedSpots} / 10 foundations prepared. {lockedSlots > 0 && (foundationUnlockingEnabled ? "Click a locked foundation to clear the next spot." : "Research 'Unlock Furnace Foundations' to prepare more foundations.")}</F.PatchStatus>
        <F.Smelter $type={props.type} data-testid={`${props.type}-smelter-field`}>
            <F.SmelterInner>
                {fieldState.smelters.map((item, index) => {
                    const cycleTimeMs = recipe?.cycleTimeMs ?? 1000
                    const fuelCost = recipe?.fuelCostPerCycle
                    const hasAutoFueling = hasTechnology("tech-auto-smelter-fueling")
                    // For manual fueling (no auto-fueling tech), always check for coal
                    // For auto-fueling, check for the selected fuel type
                    const fuelTypeToCheck = hasAutoFueling ? fieldState.fuelType : "coal"
                    const fuelQuantity = fuelCost && fuelTypeToCheck ? getItemQuantity(fuelTypeToCheck) : 0
                    const showFuelWarning = !!fuelCost && (!fuelTypeToCheck || fuelQuantity < fuelCost.quantity)

                    // Check for input (ore) availability
                    const inputRequired = recipe?.inputId
                    let inputQuantityRequired = recipe?.inputQuantity ?? 1
                    // Adjust for advanced steel technology
                    if (props.type === "steel" && hasTechnology("tech-advanced-steel")) {
                        inputQuantityRequired = 4; // Advanced steel: 4 iron plates instead of 5
                    }
                    const inputQuantityAvailable = inputRequired ? getItemQuantity(inputRequired) : 0
                    const showInputWarning = item.running && !!inputRequired && inputQuantityAvailable < inputQuantityRequired

                    // Calculate minimum fuel for one cycle based on smelter type
                    let minFuelForOneCycle: number | undefined;
                    if (fuelCost) {
                        switch (props.type) {
                            case "iron":
                                minFuelForOneCycle = ENERGY_COSTS.smeltingIron; // 0.2 MJ
                                break;
                            case "copper":
                                minFuelForOneCycle = ENERGY_COSTS.smeltingCopper; // 0.2 MJ
                                break;
                            case "stone":
                                minFuelForOneCycle = ENERGY_COSTS.smeltingStone; // 0.4 MJ
                                break;
                            case "steel":
                                minFuelForOneCycle = ENERGY_COSTS.smeltingSteel; // 0.8 MJ
                                break;
                        }
                    }

                    return (
                        <Furnace
                            key={index}
                            smelterId={item.itemId}
                            isSpinning={item.running}
                            progressMs={item.progressMs}
                            cycleTimeMs={cycleTimeMs}
                            callback={() => toggleSmelter(props.type, index)}
                            showFuelWarning={showFuelWarning}
                            showInputWarning={showInputWarning}
                            inputStarved={item.inputStarved}
                            fuelBuffer={item.fuelBuffer}
                            autoFuelingEnabled={hasAutoFueling}
                            onAddFuel={() => addFuelToSmelterBuffer(props.type, index)}
                            onRemove={() => removeSmelter(props.type, index)}
                            minFuelForOneCycle={minFuelForOneCycle}
                            fuelAvailable={fuelQuantity}
                        />
                    )
                })}

                {canAddMoreSmelters && <EmptySlot type={props.type} clickable={true} options={smelters} addedItem={addedSmelter}
                                                        onRequestCraft={requestCraft}/>}

                {Array.from({length: fillerSlots}).map((_, index) => (
                    <EmptySlot
                        key={`filler-${index}`}
                        type={props.type}
                        clickable={false}
                    />
                ))}

                {Array.from({length: lockedSlots}).map((_, index) => (
                    <F.LockedSlot key={`locked-${index}`} onClick={handleUnlockClick}>
                        Prepare foundation
                    </F.LockedSlot>
                ))}

            </F.SmelterInner>
            {hasAutoFueling && <FuelSelection type={props.type} options={fuelOptions} selectedFuel={fieldState.fuelType}/>}
        </F.Smelter>
        {lockedSlots > 0 && <SmeltingUnlockModal
            resourceType={props.type}
            clearedSpots={clearedSpots}
            isOpen={showUnlockModal}
            onClose={() => setShowUnlockModal(false)}
            onComplete={handleUnlockComplete}
        />}
    </F.FieldContainer>
}

type FurnaceProps = {
    smelterId: string,
    isSpinning: boolean,
    progressMs: number,
    cycleTimeMs: number,
    callback: () => void,
    showFuelWarning: boolean,
    showInputWarning?: boolean,
    inputStarved?: boolean,
    fuelBuffer?: number,
    autoFuelingEnabled?: boolean,
    onAddFuel?: () => void,
    onRemove?: () => void,
    minFuelForOneCycle?: number,
    fuelAvailable?: number,
}

function Furnace({smelterId, isSpinning, progressMs, cycleTimeMs, callback, showFuelWarning, showInputWarning, inputStarved, fuelBuffer, autoFuelingEnabled, onAddFuel, onRemove, minFuelForOneCycle, fuelAvailable}: FurnaceProps) {
    const {inventory} = useInventory();
    const fuelAmountPerClick = inventory.fuelAmountPerClick ?? 1;

    // Look up the actual item to get its name and power use
    const smelterItem = inventory.inventory.find(item => item.item.id === smelterId);
    const smelterName = smelterItem?.item.name ?? "Smelter";
    const powerUse = smelterItem?.item.powerUse;

    // Calculate production rate based on recipe (passed via cycleTimeMs)
    // For smelters, we assume 1 output per cycle for most recipes
    // Steel is 5 iron -> 1 steel, but still 1 output
    const productionRate = cycleTimeMs > 0 ? (1 / cycleTimeMs) * 1000 : undefined;

    return <MachineCard
        label={smelterName}
        running={isSpinning}
        progressMs={progressMs}
        cycleTimeMs={cycleTimeMs}
        onToggle={callback}
        showFuelWarning={showFuelWarning}
        showInputWarning={showInputWarning}
        inputStarved={inputStarved}
        fuelBuffer={fuelBuffer}
        autoFuelingEnabled={autoFuelingEnabled}
        onAddFuel={onAddFuel}
        fuelAmountPerClick={fuelAmountPerClick}
        onRemove={onRemove}
        minFuelForOneCycle={minFuelForOneCycle}
        machineItemId={smelterId}
        productionRate={productionRate}
        powerUse={powerUse}
        fuelAvailable={fuelAvailable}
    />
}


export function Smelting() {
    const {hasTechnology} = useInventory();
    const smelterTypes = ["iron", "copper", "stone", ...(hasTechnology("tech-steel-smelting") ? ["steel"] : [])];

    return (
        <>
            <h2>Smelting</h2>
            {!hasTechnology("tech-steel-smelting") && (
                <p style={{color: "#6c757d"}}>Research Steel Smelting to unlock the steel furnace queue.</p>
            )}
            <FuelControl />
            {smelterTypes.map(type => (
                <Smelter key={type} type={type}/>
            ))}
        </>
    );
}